import { useCallback, useState } from 'react'
import type { MetadatoArchivo } from '@/lib/schemas/publicaciones'
import {
  abrirPicker,
  blurhashDe,
  driveFalso,
  metadataDe,
  pickerConfigurado,
  type ArchivoElegido,
} from './picker'
import { useTokenDrive } from './useTokenDrive'
import { esImagen, validarArchivo, type ResultadoValidacion } from './validar'

/** Un archivo elegido de Drive, con lo que se guarda y cómo validó. */
export interface ArchivoDeDrive {
  id: string
  nombre: string
  metadato: MetadatoArchivo
  validacion: ResultadoValidacion
}

export type EsperaArchivo = 'imagen' | 'video' | 'imagen-o-video'

interface Opciones {
  carpetaId: string | null
  multiple: boolean
  espera: EsperaArchivo
  titulo: string
}

/**
 * "Elegir de Drive" de punta a punta (Fase 6): pide el token (y el permiso,
 * la primera vez), abre el selector acotado a la carpeta del cliente, lee la
 * metadata, calcula el degradado de las imágenes y valida cada archivo.
 *
 * `estado` = 'permiso' cuando falta el permiso de Drive: la interfaz ofrece
 * darlo (`pedirPermiso`), que lleva a Google y vuelve a la misma página.
 */
export function useElegirDeDrive() {
  const { obtener, pedirPermiso, tieneGoogle } = useTokenDrive()
  const [estado, setEstado] = useState<'listo' | 'abriendo' | 'leyendo' | 'permiso'>(
    'listo',
  )
  const [error, setError] = useState<string | null>(null)

  const falso = driveFalso()
  const disponible = Boolean(falso) || (pickerConfigurado() && tieneGoogle)

  const elegir = useCallback(
    async (o: Opciones): Promise<ArchivoDeDrive[]> => {
      setError(null)
      try {
        let elegidos: ArchivoElegido[]
        let leer: (a: ArchivoElegido) => Promise<ArchivoDeDrive>

        const simulado = driveFalso()
        if (simulado) {
          elegidos = simulado.selector
            ? await abrirPicker({
                token: 'falso',
                carpetaId: o.carpetaId,
                modo: 'archivos',
                multiple: o.multiple,
                titulo: o.titulo,
              })
            : simulado.elegidos
          leer = (a) => {
            const m = simulado.metadata[a.id] ?? {
              mime: a.mime,
              peso: a.peso,
              ancho: null,
              alto: null,
              duracionSeg: null,
              miniatura: null,
            }
            return Promise.resolve(armar(a, { ...m, blurhash: null }, o.espera))
          }
        } else {
          setEstado('abriendo')
          const token = await obtener()
          if (!token) {
            setEstado('permiso')
            return []
          }
          elegidos = await abrirPicker({
            token,
            carpetaId: o.carpetaId,
            modo: 'archivos',
            multiple: o.multiple,
            titulo: o.titulo,
          })
          leer = async (a) => {
            const m = await metadataDe(a.id, token)
            const blurhash = esImagen(m.mime) ? await blurhashDe(a.id, token) : null
            return armar(a, { ...m, blurhash }, o.espera)
          }
        }

        if (elegidos.length === 0) return []
        setEstado('leyendo')
        return await Promise.all(elegidos.filter((a) => !a.esCarpeta).map(leer))
      } catch (e) {
        setError(
          e instanceof Error
            ? `No pudimos abrir Drive: ${e.message}`
            : 'No pudimos abrir Drive. Probá de nuevo.',
        )
        return []
      } finally {
        setEstado((x) => (x === 'permiso' ? x : 'listo'))
      }
    },
    [obtener],
  )

  /**
   * Una carpeta o documentos, sin la validación de una publicación: la
   * carpeta de un cliente, el documento de una planificación, los enlaces de
   * un recurso. Devuelve nombre, tipo y enlace de cada uno.
   */
  const elegirEnlaces = useCallback(
    async (o: {
      carpetaId: string | null
      modo: 'archivos' | 'carpeta'
      multiple: boolean
      tipos?: string
      titulo: string
    }): Promise<ArchivoElegido[]> => {
      setError(null)
      try {
        const simulado = driveFalso()
        if (simulado)
          return simulado.selector
            ? await abrirPicker({ token: 'falso', ...o })
            : simulado.elegidos
        setEstado('abriendo')
        const token = await obtener()
        if (!token) {
          setEstado('permiso')
          return []
        }
        return await abrirPicker({ token, ...o })
      } catch (e) {
        setError(
          e instanceof Error
            ? `No pudimos abrir Drive: ${e.message}`
            : 'No pudimos abrir Drive. Probá de nuevo.',
        )
        return []
      } finally {
        setEstado((x) => (x === 'permiso' ? x : 'listo'))
      }
    },
    [obtener],
  )

  return { elegir, elegirEnlaces, pedirPermiso, estado, error, disponible }
}

function armar(
  a: ArchivoElegido,
  metadato: MetadatoArchivo,
  espera: EsperaArchivo,
): ArchivoDeDrive {
  return {
    id: a.id,
    nombre: a.nombre,
    metadato,
    validacion: validarArchivo(metadato, espera),
  }
}
